import { Modal } from "antd";
import {
  CloseOutlined,
  TagsOutlined,
  ShopOutlined,
  SwapOutlined,
  TrademarkCircleOutlined,
  ColumnWidthOutlined,
  BgColorsOutlined,
  BarcodeOutlined,
  StarOutlined,
  GiftOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  CalculatorOutlined,
  PercentageOutlined,
  CrownOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { motion } from "framer-motion";

// ---------------------------------------------------------------------------
// Design tokens — gem-toned gradients instead of flat tints. Kept in one
// place so the palette stays consistent if this pattern is reused elsewhere.
// ---------------------------------------------------------------------------
const gradients = {
  header: "linear-gradient(120deg, #0B1F3D 0%, #1E3A8A 55%, #5B21B6 100%)",
  goldBar: "linear-gradient(90deg, #F5D06F, #B8860B 50%, #F5D06F)",
  trigger: "linear-gradient(135deg, #4C1D95, #1E3A8A)",
  cancel: "linear-gradient(135deg, #FB7185, #BE123C)",
};

// Category system: each field family gets its own saturated gradient chip
// (icon), plus a light gradient wash (row background) and accent color
// (left border / text) derived from the same hue.
const categories = {
  identity: {
    chip: "#4338CA",
    light: "#ecf0f8",
    accent: "#4338CA",
    glow: "rgba(67,56,202,0.35)",
  },
  attribute: {
    chip: "#047857",
    light: "#eff7f3fd",
    accent: "#047857",
    glow: "rgba(4,120,87,0.3)",
  },
  classification: {
    chip: "#B45309",
    light: "#f7f5ee",
    accent: "#B45309",
    glow: "rgba(180,83,9,0.3)",
  },
  pricing: {
    chip: "#1D4ED8",
    light: "#ecf1f7fb",
    accent: "#1D4ED8",
    glow: "rgba(29,78,216,0.3)",
  },
  tax: {
    chip: "#BE185D",
    light: "#f7eef3",
    accent: "#BE185D",
    glow: "rgba(190,24,93,0.3)",
  },
};

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: gradients.header,
    padding: "16px 20px",
    borderRadius: "12px 12px 0 0",
    margin: "-20px -24px 0",
    position: "relative",
    overflow: "hidden",
  },
  goldBar: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
    background: gradients.goldBar,
    backgroundSize: "200% 100%",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  iconBadge: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 17,
    flexShrink: 0,
  },
  headerTitle: { color: "#fff", fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
  headerSubtitle: { color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 2 },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "none",
    background: "rgba(255,255,255,0.14)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  body: { padding: "24px 4px 4px" },
  columns: { display: "flex", alignItems: "stretch", gap: 22 },
  sectionPill: {
    display: "inline-block",
    fontSize: 12,
    fontWeight: 700,
    color: "#4338CA",
    background: "linear-gradient(90deg, #EEF2FF, #F5F3FF)",
    border: "1px solid #E0E7FF",
    borderRadius: 999,
    padding: "4px 12px",
    marginBottom: 14,
  },
  col: { flex: 1, minWidth: 0 },
  divider: { width: 1, alignSelf: "stretch", background: "#E5E8EC" },
  rowItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    marginBottom: 8,
    borderRadius: 10,
  },
  iconChip: {
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    flexShrink: 0,
  },
  rowLabel: { fontSize: 13, color: "#363a41", flex: 1, minWidth: 0, fontWeight: 500 },
  rowValue: {
    fontSize: 13.5,
    color: "#1F2937",
    fontWeight: 700,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    wordBreak: "break-word",
    maxWidth: "50%",
  },
  netAmountCard: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(100deg, #FEF3C7, #FFFBEB)",
    border: "1px solid #FDE68A",
    borderRadius: 10,
    padding: "12px 16px",
  },
  netAmountLeft: { display: "flex", alignItems: "center", gap: 12 },
  totalLabel: { fontSize: 13.5, fontWeight: 700, color: "#78350F" },
  totalValue: { fontSize: 19, fontWeight: 800, color: "#78350F", fontVariantNumeric: "tabular-nums" },
  footer: { display: "flex", justifyContent: "flex-end", marginTop: 24 },
};

const rowVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.035, duration: 0.3, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Row = ({ icon, category, label, value, index }) => {
  const c = categories[category];
  return (
    <motion.div
      style={{
        ...styles.rowItem,
        borderLeft: `3px solid ${c.accent}`,
        background: c.light,
      }}
      variants={rowVariants}
      custom={index}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        style={{ ...styles.iconChip, color: c.chip }}
        whileHover={{ scale: 1.18, rotate: 8 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: "spring", stiffness: 400, damping: 15 }}
      >
        {icon}
      </motion.div>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{value ?? "-"}</span>
    </motion.div>
  );
};

const formatCurrency = (n) => `\u20B9${Number(n || 0).toFixed(2)}`;

const productFields = [
  { key: "ProdName", label: "Product Name", icon: <ShopOutlined />, category: "identity" },
  { key: "PTYPE", label: "Purchase Type", icon: <SwapOutlined />, category: "attribute" },
  { key: "CompName", label: "Brand Name", icon: <TrademarkCircleOutlined />, category: "identity" },
  { key: "Prodsize", label: "Size", icon: <ColumnWidthOutlined />, category: "attribute" },
  { key: "COLOUR", label: "Color", icon: <BgColorsOutlined />, category: "attribute" },
  { key: "HSNCODE", label: "HSN Code", icon: <BarcodeOutlined />, category: "classification" },
  { key: "FITTING", label: "Fashion", icon: <StarOutlined />, category: "classification" },
  { key: "SLEVES", label: "Offer", icon: <GiftOutlined />, category: "classification" },
];

const ItemDetailsPopover = ({ record, smCodeRef }) => {
  const [visible, setVisible] = useState(false);

  const gst = record?.GSTApplied ?? 0;
  const basicAmt = (record?.stock ?? 0) * (record?.offer ?? 0) * (100 / (100 + gst));
  const gstAmt = basicAmt * (gst / 100);
  const cGstRate = gst / 2;
  const cGstAmt = gstAmt / 2;
  const sGstRate = gst / 2;
  const sGstAmt = gstAmt / 2;
  const netAmt = (record?.stock ?? 0) * (record?.offer ?? 0);

  const pricingFields = [
    { label: "Act Rate", icon: <DollarCircleOutlined />, category: "pricing", value: formatCurrency(record?.offer) },
    { label: "Rate", icon: <WalletOutlined />, category: "pricing", value: formatCurrency(record?.CPrice) },
    { label: "Basic Amt", icon: <CalculatorOutlined />, category: "pricing", value: formatCurrency(basicAmt) },
    { label: `GST @${Number(gst).toFixed(2)}%`, icon: <PercentageOutlined />, category: "tax", value: formatCurrency(gstAmt) },
    { label: `CGST @${Number(cGstRate).toFixed(2)}%`, icon: <PercentageOutlined />, category: "tax", value: formatCurrency(cGstAmt) },
    { label: `SGST @${Number(sGstRate).toFixed(2)}%`, icon: <PercentageOutlined />, category: "tax", value: formatCurrency(sGstAmt) },
  ];

  const closeAndRefocus = () => {
    setVisible(false);
    smCodeRef.current?.focus();
  };

  return (
    <>
      <motion.button
        onClick={() => {
          setVisible(true);
          smCodeRef.current?.focus();
        }}
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          border: "none",
          background: gradients.trigger,
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 15,
        }}
        whileHover={{ scale: 1.12, boxShadow: "0 6px 16px rgba(76,29,149,0.45)" }}
        whileTap={{ scale: 0.92 }}
        animate={{ boxShadow: ["0 3px 10px rgba(76,29,149,0.25)", "0 3px 14px rgba(76,29,149,0.4)", "0 3px 10px rgba(76,29,149,0.25)"] }}
        transition={{ boxShadow: { duration: 2.5, repeat: Infinity }, default: { type: "spring", stiffness: 400, damping: 14 } }}
      >
        <TagsOutlined />
      </motion.button>

      <Modal
        open={visible}
        onCancel={closeAndRefocus}
        onOk={closeAndRefocus}
        footer={null}
        width={750}
        centered
        closable={false}
        maskStyle={{ backdropFilter: "blur(8px)", backgroundColor: "rgba(11,15,30,0.35)" }}
        styles={{
          content: {
            padding: 20,
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(30,27,75,0.35), 0 10px 24px rgba(0,0,0,0.15)",
          },
        }}
      >
        {/* ===== Header ===== */}
        <div style={styles.header}>
          <motion.div
            style={{ ...styles.goldBar }}
            animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
          <div style={styles.headerLeft}>
            <motion.div
              style={styles.iconBadge}
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <TagsOutlined />
            </motion.div>
            <div>
              <div style={styles.headerTitle}>Item Details</div>
              <div style={styles.headerSubtitle}>Code {record?.DesignCode ?? "-"}</div>
            </div>
          </div>
          <motion.button
            style={styles.closeBtn}
            onClick={closeAndRefocus}
            aria-label="Close"
            whileHover={{ rotate: 90, backgroundColor: "rgba(255,255,255,0.25)" }}
            whileTap={{ scale: 0.85 }}
            transition={{ duration: 0.25 }}
          >
            <CloseOutlined style={{ fontSize: 12 }} />
          </motion.button>
        </div>

        {/* ===== Body ===== */}
        <div style={styles.body}>
          <div style={styles.columns}>
            {/* Product details */}
            <div style={styles.col}>
              <span style={styles.sectionPill}>Product details</span>
              {productFields.map((f, i) => (
                <Row key={f.key} icon={f.icon} category={f.category} label={f.label} value={record?.[f.key]} index={i} />
              ))}
            </div>

            <div style={styles.divider} />

            {/* Pricing summary */}
            <div style={styles.col}>
              <span style={{ ...styles.sectionPill, color: "#B45309", background: "linear-gradient(90deg, #FFFBEB, #FEF3C7)", borderColor: "#FDE68A" }}>
                Pricing summary
              </span>
              {pricingFields.map((f, i) => (
                <Row key={f.label} icon={f.icon} category={f.category} label={f.label} value={f.value} index={i} />
              ))}

              <motion.div
                style={styles.netAmountCard}
                initial={{ opacity: 0, y: 8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: pricingFields.length * 0.035 + 0.1, duration: 0.3 }}
              >
                <div style={styles.netAmountLeft}>
                  <motion.div
                    style={{ ...styles.iconChip, background: "linear-gradient(135deg, #FBBF24, #B8860B)" }}
                    animate={{
                      scale: [1, 1.14, 1],
                      boxShadow: ["0 0 0 rgba(184,134,11,0)", "0 0 12px rgba(184,134,11,0.55)", "0 0 0 rgba(184,134,11,0)"],
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <CrownOutlined />
                  </motion.div>
                  <span style={styles.totalLabel}>Net Amount</span>
                </div>
                <span style={styles.totalValue}>{formatCurrency(netAmt)}</span>
              </motion.div>
            </div>
          </div>

          {/* ===== Footer ===== */}
          <div style={styles.footer}>
            <motion.button
              onClick={closeAndRefocus}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: gradients.cancel,
                border: "none",
                color: "#fff",
                borderRadius: 10,
                padding: "10px 20px",
                fontWeight: 700,
                fontSize: 13.5,
                cursor: "pointer",
              }}
              whileHover={{ y: -2, boxShadow: "0 10px 22px rgba(190,18,60,0.45)" }}
              whileTap={{ scale: 0.96, y: 0 }}
              animate={{ boxShadow: "0 4px 12px rgba(190,18,60,0.3)" }}
              transition={{ duration: 0.2 }}
            >
              <CloseCircleOutlined />
              Cancel
            </motion.button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ItemDetailsPopover;